import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import type { XkEvent } from '@/lib/api/types';
import { queryKeys } from '@/lib/api/queryKeys';
import { Skeleton } from '@/components/ui/Skeleton';
import { MultiSelect } from '@/components/ui/MultiSelect';
import { useT } from '@/lib/i18n/useT';
import { factsApi, type EventFacts } from '@/features/insights/factsApi';
import { CompareMatrix, type CompareGroupInput } from '@/features/insights/charts/CompareMatrix';
import { MAX_GROUPS } from '@/features/insights/stats';

/** Cuántos eventos entran en la comparación si no se elige nada: los más recientes. */
const DEFAULT_PICK = 4;

/**
 * COMPARAR — los eventos del proyecto unos contra otros, cada uno como un
 * grupo: la misma matriz que Insights, con sus márgenes y su test frente al
 * evento de referencia. Un proyecto puede tener cincuenta eventos, así que
 * cuáles se comparan se elige en un selector; por defecto, los más
 * recientes con datos. Uno por venir no tiene nada que comparar.
 */
export function CompareTab({ events }: { groupId: number; events: XkEvent[] }) {
  const t = useT();
  const eventIds = useMemo(() => events.map((e) => e.id).sort((a, b) => a - b), [events]);
  const factsQ = useQuery({
    queryKey: queryKeys.insights.facts(eventIds),
    queryFn: () => factsApi.forEvents(eventIds),
    enabled: eventIds.length > 0,
    staleTime: 30_000,
  });
  const withData = useMemo<EventFacts[]>(
    () =>
      [...(factsQ.data?.events ?? [])]
        .filter((f) => f.funnel.interactions > 0 || f.funnel.prizesDelivered > 0)
        .sort((a, b) => (b.event.startDate ?? '').localeCompare(a.event.startDate ?? '')),
    [factsQ.data],
  );
  const [picked, setPicked] = useState<number[]>([]);
  const chosen = picked.length ? withData.filter((f) => picked.includes(f.event.id)) : withData.slice(0, DEFAULT_PICK);
  const groups = useMemo<CompareGroupInput[]>(
    () =>
      [...chosen]
        .sort((a, b) => (a.event.startDate ?? '').localeCompare(b.event.startDate ?? ''))
        .map((f) => ({ key: String(f.event.id), label: f.event.name, facts: [f] })),
    [chosen],
  );

  if (factsQ.isLoading) return <Skeleton className="h-64 w-full" />;
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-[14px] border p-4" style={{ background: 'var(--color-dash-paper)', borderColor: 'var(--color-dash-border)' }}>
        <p className="text-sm" style={{ color: 'var(--color-dash-fg-2)' }}>
          <span className="dash-mono mr-2 text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: 'var(--color-dash-olive)' }}>{t('compare.projectKicker')}</span>
          {t('compare.projectIntro', { n: withData.length, total: events.length })}
        </p>
        <label className="block max-w-xl">
          <span className="mb-1.5 block text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-fg-muted)]">{t('compare.pickEvents', { max: MAX_GROUPS })}</span>
          <MultiSelect
            options={withData.map((f) => ({ value: f.event.id, label: f.event.name, hint: f.event.startDate ? dayjs(f.event.startDate).format('MMM D, YYYY') : undefined }))}
            value={picked}
            onChange={(next) => setPicked(next.slice(0, MAX_GROUPS))}
            placeholder={t('compare.pickDefault', { n: Math.min(DEFAULT_PICK, withData.length) })}
            searchPlaceholder={t('insights.filter.searchEvents')}
            summary={(n) => t('insights.filter.eventsPicked', { n })}
            clearLabel={t('insights.filter.clear')}
            emptyLabel={t('insights.filter.noMatch')}
          />
        </label>
      </div>
      <CompareMatrix groups={groups} />
    </div>
  );
}
